import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Download, BarChart3, FileText, Calendar, Building2, TrendingUp, TrendingDown } from "lucide-react";

import { reportsData, getWeeklyComparisonChartData, getSpendComparisonData, getCTRComparisonData, getCostComparisonData, getMetricsDistribution } from "../data/reportsData";
import MetricCard from "./MetricCard";
import HighlightCard from "./HighlightCard";
import { WeeklyComparisonChart, GrowthComparisonChart } from "./WeeklyComparisonChart";
import SpendPieChart from "./SpendPieChart";
import ConversionGrowthChart from "./ConversionGrowthChart";
import CostEfficiencyChart from "./CostEfficiencyChart";
import CTRChart from "./CTRChart";
import ForecastCard from "./ForecastCard";

// 7-Day Comparison Metric Card with growth indicators
const ComparisonMetricCard = ({ label, firstValue, lastValue, prefix = "", suffix = "", invertGrowth = false }) => {
  const formatValue = (val) => {
    if (typeof val === "number") {
      if (val >= 1000) return val.toLocaleString();
      if (val % 1 !== 0) return val.toFixed(2);
      return val.toString();
    }
    return val;
  };

  const change = firstValue > 0 ? ((lastValue - firstValue) / firstValue * 100).toFixed(0) : (lastValue > 0 ? "∞" : "0");
  const isPositive = invertGrowth ? lastValue < firstValue : lastValue > firstValue;
  const isNeutral = lastValue === firstValue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm"
    >
      <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-3">{label}</p>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <p className="text-xs text-slate-400 mb-1">First 7 Days</p>
          <p className="text-lg font-heading font-semibold text-slate-600">
            {prefix}{formatValue(firstValue)}{suffix}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-400 mb-1">Last 7 Days</p>
          <p className="text-lg font-heading font-semibold text-slate-800">
            {prefix}{formatValue(lastValue)}{suffix}
          </p>
        </div>
      </div>
      {!isNeutral && (
        <div className={`flex items-center gap-1 text-sm font-semibold ${
          isPositive ? 'text-emerald-600' : 'text-red-600'
        }`}>
          {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span>{isPositive ? '+' : ''}{change}%</span>
        </div>
      )}
    </motion.div>
  );
};

export const Dashboard = () => {
  const [selectedClient, setSelectedClient] = useState("Poplar Creek");
  const [isExporting, setIsExporting] = useState(false);
  const dashboardRef = useRef(null);

  const report = reportsData[selectedClient];
  const weeklyData = getWeeklyComparisonChartData(report);
  const spendData = getSpendComparisonData(report);
  const ctrData = getCTRComparisonData(report);
  const costData = getCostComparisonData(report);
  const pieData = getMetricsDistribution(report);

  const handleExportPDF = async () => {
    if (!dashboardRef.current || isExporting) return;
    
    setIsExporting(true);
    
    try {
      const element = dashboardRef.current;
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#FAFBFC',
        logging: false,
        windowWidth: 1400,
        windowHeight: element.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgScaledHeight = imgHeight * ratio;
      const totalPages = Math.ceil(imgScaledHeight / pdfHeight);
      
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        
        const srcY = page * (pdfHeight / ratio);
        const srcHeight = Math.min(pdfHeight / ratio, imgHeight - srcY);
        
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = imgWidth;
        pageCanvas.height = srcHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, srcY, imgWidth, srcHeight, 0, 0, imgWidth, srcHeight);
        
        const pageImgData = pageCanvas.toDataURL('image/png');
        pdf.addImage(pageImgData, 'PNG', 0, 0, pdfWidth, srcHeight * ratio);
      }
      
      pdf.save(`Google_Ads_Report_${selectedClient.replace(' ', '_')}_March_2026.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const clients = Object.keys(reportsData);
  const first7 = report.weeklyComparison.first7Days;
  const last7 = report.weeklyComparison.last7Days;

  return (
    <div className="dashboard-container min-h-screen">
      {/* Header */}
      <header className="dashboard-header px-6 md:px-12 py-4" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-800">
                Ad Performance Report
              </h1>
              <p className="text-sm text-slate-500">
                {report.period}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="bg-slate-100 p-1 rounded-lg flex" data-testid="client-selector">
              {clients.map((client) => (
                <button
                  key={client}
                  onClick={() => setSelectedClient(client)}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    selectedClient === client
                      ? 'tab-active'
                      : 'tab-inactive'
                  }`}
                  data-testid={`client-tab-${client.toLowerCase().replace(' ', '-')}`}
                >
                  {client}
                </button>
              ))}
            </div>

            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="btn-download px-4 py-2 flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main ref={dashboardRef} className="px-6 md:px-12 py-8 max-w-7xl mx-auto" id="pdf-export-container">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedClient}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {/* Report Header */}
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="report-badge">
                  <Building2 className="w-4 h-4" />
                  {report.title}
                </div>
                <div className="report-badge">
                  <Calendar className="w-4 h-4" />
                  {report.period}
                </div>
                <div className="report-badge">
                  <FileText className="w-4 h-4" />
                  Monthly Report
                </div>
              </div>
            </div>

            {/* SECTION 1: Month-to-Date Performance (No growth indicators) */}
            <section className="mb-10" data-testid="mtd-section">
              <div className="mb-6">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 mb-1">
                  Month-to-Date Performance
                </h2>
                <p className="text-slate-500">
                  Overall campaign performance for {report.period}
                </p>
              </div>
              <div className="snapshot-grid">
                {Object.entries(report.snapshot).map(([key, data]) => (
                  <MetricCard
                    key={key}
                    label={data.label}
                    value={data.value}
                    prefix={data.prefix || ""}
                    suffix={data.suffix || ""}
                  />
                ))}
              </div>
            </section>

            <div className="section-divider" />

            {/* SECTION 2: 7-Day Performance Comparison (WITH growth indicators) */}
            <section className="mb-10" data-testid="comparison-section">
              <div className="mb-6">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 mb-1">
                  7-Day Performance Comparison
                </h2>
                <p className="text-slate-500">
                  Comparing <strong>First 7 Days</strong> (campaign start) vs <strong>Last 7 Days</strong> (recent performance)
                </p>
              </div>

              {/* Comparison Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <ComparisonMetricCard
                  label="Impressions"
                  firstValue={first7.impressions}
                  lastValue={last7.impressions}
                />
                <ComparisonMetricCard
                  label="Clicks"
                  firstValue={first7.clicks}
                  lastValue={last7.clicks}
                />
                <ComparisonMetricCard
                  label="CTR"
                  firstValue={first7.ctr}
                  lastValue={last7.ctr}
                  suffix="%"
                />
                <ComparisonMetricCard
                  label="Conversions"
                  firstValue={first7.conversions}
                  lastValue={last7.conversions}
                />
                <ComparisonMetricCard
                  label="Conversion Rate"
                  firstValue={first7.conversionRate}
                  lastValue={last7.conversionRate}
                  suffix="%"
                />
                <ComparisonMetricCard
                  label="Cost per Conversion"
                  firstValue={first7.costPerConversion}
                  lastValue={last7.costPerConversion}
                  prefix="$"
                  invertGrowth={true}
                />
                <ComparisonMetricCard
                  label="Avg CPC"
                  firstValue={first7.avgCpc}
                  lastValue={last7.avgCpc}
                  prefix="$"
                  invertGrowth={true}
                />
                <ComparisonMetricCard
                  label="Spend"
                  firstValue={first7.spend}
                  lastValue={last7.spend}
                  prefix="$"
                />
              </div>

              {/* Charts */}
              <div className="chart-grid mb-6">
                <ConversionGrowthChart
                  firstValue={first7.conversions}
                  lastValue={last7.conversions}
                  title="Conversion Growth"
                />
                <SpendPieChart 
                  data={pieData}
                  title="Spend Distribution"
                />
              </div>

              <div className="chart-grid mb-6">
                <WeeklyComparisonChart
                  data={weeklyData}
                  title="Traffic & Conversions"
                />
                <CTRChart
                  data={ctrData}
                  title="CTR & Conversion Rate"
                />
              </div>

              <div className="chart-grid">
                <CostEfficiencyChart
                  data={costData}
                  title="Cost Efficiency"
                />
                <GrowthComparisonChart
                  data={spendData}
                  title="Spend Comparison"
                  showGrowthColors={false}
                />
              </div>
            </section>

            <div className="section-divider" />

            {/* Key Highlights */}
            <section className="mb-10" data-testid="highlights-section">
              <div className="mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-1">
                  Key Performance Highlights
                </h2>
                <p className="text-slate-500 text-sm">
                  Major improvements from the 7-day comparison
                </p>
              </div>
              <div className="highlights-grid">
                {report.highlights.map((highlight, index) => (
                  <HighlightCard
                    key={index}
                    title={highlight.title}
                    description={highlight.description}
                    metric={highlight.metric}
                    isPositive={highlight.isPositive}
                    delay={index * 0.08}
                  />
                ))}
              </div>
            </section>

            <div className="section-divider" />

            {/* Forecast Section */}
            <section className="mb-10" data-testid="forecast-section">
              <ForecastCard 
                forecast={report.forecast}
                title="Performance Forecast"
              />
            </section>

            {/* Campaign Insights */}
            <section className="mb-8" data-testid="campaigns-section">
              <div className="mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-800 mb-1">
                  Campaign Structure
                </h2>
                <p className="text-slate-500 text-sm">
                  Active campaigns and their performance status
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {report.campaigns.map((campaign, index) => (
                  <motion.div
                    key={campaign.name}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="metric-card p-5"
                    data-testid={`campaign-card-${campaign.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-heading font-semibold text-slate-800">
                        {campaign.name}
                      </h4>
                      <span className={`status-badge ${campaign.status}`}>
                        {campaign.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">
                      {campaign.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Footer */}
            <footer className="pt-8 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-400">
                Report generated for {report.title} • {report.period} • Google Ads Performance Report
              </p>
            </footer>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default Dashboard;
